import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBackOutlined';
import { useHistory } from 'react-router-dom';
import { SubmitHandler } from 'react-hook-form';
import { MenuProvider } from '../../menuContext';
import AdministrationLayout from './Layout';
import MultiStepForm from '../../components/Forms/MultiStepForm';
import { AppContext } from '../../context';
import { Step1, Step2, Step3, Step4, Step5 } from '../../components/Forms/AdministrationUserForm';
import {
  AdministrationSortingCenterUserRoleForPost,
  AdministrationUserCreateForm,
  AdministrationUserForPost,
  SortingCenter,
} from '../../types';
import { notify } from '../../components/Notifier';
import {
  postAdministrationUserSortingCenterUserRole,
  putAdministrationUserSortingCenterUserRole,
} from '../../services/apiOperations';
import { transformObjectId } from '../../services/apiPlatformTransformer';
import { Routes } from '../../routes';
import { goBack } from '../../utils/history';

const useStyles = makeStyles({
  headerTitle: {
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
  },
  title: {
    margin: 0,
  },
});

const AdministrationUserCreate: React.FC = () => {
  const context = useContext(AppContext);
  const history = useHistory();
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'administration.users.create' });
  const sortingCenters = context.getSortingCenters?.();

  const initialData = {};
  const eligibleSortingCenter: SortingCenter[] = [];
  sortingCenters?.map((sortingCenterParent: SortingCenter, indexParent) => {
    eligibleSortingCenter.push(sortingCenterParent);
    sortingCenterParent.childrenSortingCenter?.map((sortingCenterChild: SortingCenter, indexChild) => {
      eligibleSortingCenter.push(sortingCenterChild);
      return null;
    });
    return null;
  });

  const onSubmit: SubmitHandler<AdministrationUserCreateForm> = async (
    userCreateForm: AdministrationUserCreateForm,
  ) => {
    const roles: AdministrationSortingCenterUserRoleForPost[] = [];
    userCreateForm.sortingCenterUserRole?.map((role: string, sortingCenterId: number) => {
      const sortingCenter = eligibleSortingCenter.find(
        sortingCenter => transformObjectId(sortingCenter) === String(sortingCenterId),
      );
      if (role && sortingCenter) {
        roles.push({
          role,
          sortingCenter: sortingCenter['@id'],
        });
      }
      return null;
    });

    if (userCreateForm.userId) {
      return putAdministrationUserSortingCenterUserRole(userCreateForm.userId, roles)
        .then(() => {
          notify({
            message: t('success'),
            severity: 'success',
          });
          goBack(Routes.AdministrationUsers, history);
        })
        .catch(() => {
          notify({
            message: t('errorGeneric'),
            severity: 'error',
          });
        });
    } else {
      const newUser: AdministrationUserForPost = {
        email: userCreateForm.email ?? '',
        firstName: userCreateForm.firstName ?? '',
        lastName: userCreateForm.lastName ?? '',
        plainPassword: Math.random().toString(36).slice(-12),
      };

      return postAdministrationUserSortingCenterUserRole(newUser, roles)
        .then(() => {
          notify({
            message: t('success'),
            severity: 'success',
          });
          goBack(Routes.AdministrationUsers, history);
        })
        .catch(() => {
          notify({
            message: t('errorGeneric'),
            severity: 'error',
          });
        });
    }
  };

  const steps = [
    { label: t('step1.title'), component: Step1 },
    { label: t('step2.title'), component: Step2, requiredFieldsLegend: true },
    { label: t('step3.title'), component: Step3, requiredFieldsLegend: true },
    { label: t('step4.title'), component: Step4 },
    { label: t('step5.title'), component: Step5 },
  ];

  return (
    <>
      <MenuProvider>
        <AdministrationLayout>
          <div className={classes.headerTitle}>
            <Typography variant="h1" className={classes.title}>
              {t('title')}
            </Typography>
          </div>
          <MultiStepForm
            formName="AdministrationUserCreate"
            backRoute="/administration/users"
            backIcon={<ArrowBackIcon />}
            onSubmit={onSubmit}
            steps={steps}
            initialData={initialData}
          />
        </AdministrationLayout>
      </MenuProvider>
    </>
  );
};

export default AdministrationUserCreate;
